/* Progressive enhancement: static product information and native forms work without this file. */
if (!customElements.get('ganvu-hero')) {
  customElements.define('ganvu-hero', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const {signal} = this.controller;
      const reduce = matchMedia('(prefers-reduced-motion: reduce)');
      const scene = this.querySelector('.gv-scene');
      const depth = this.querySelector('.gv-product-depth');
      const silhouette = this.querySelector('.gv-silhouette');
      let enabled = this.dataset.motion === 'true' && !reduce.matches;
      let active = false, frame = 0, pointerX = 0, pointerY = 0;
      const paint = () => {
        frame = 0;
        if (!active || !enabled) return;
        const distance = Math.min(1, Math.max(0, -scene.getBoundingClientRect().top / scene.offsetHeight));
        depth?.style.setProperty('--gv-product-y', `${(distance * 13 + pointerY * 3).toFixed(2)}px`);
        depth?.style.setProperty('--gv-product-x', `${(pointerX * 4).toFixed(2)}px`);
        depth?.style.setProperty('--gv-product-r', `${(distance * -1.2 + pointerX * .55).toFixed(2)}deg`);
        silhouette?.style.setProperty('--gv-shadow-y', `${(distance * 5 - pointerY).toFixed(2)}px`);
        silhouette?.style.setProperty('--gv-shadow-x', `${(-pointerX * 3).toFixed(2)}px`);
      };
      const schedule = () => { if (enabled && active && !frame) frame = requestAnimationFrame(paint); };
      this.observer = new IntersectionObserver(entries => {
        active = entries[0].isIntersecting;
        this.dataset.active = String(active && enabled && !document.hidden);
        schedule();
      });
      this.observer.observe(scene);
      window.addEventListener('scroll', schedule, {passive:true, signal});
      window.addEventListener('resize', schedule, {passive:true, signal});
      this.addEventListener('pointermove', event => {
        if (event.pointerType !== 'mouse' || !enabled || !active) return;
        const rect = scene.getBoundingClientRect();
        pointerX = (event.clientX - rect.left) / rect.width * 2 - 1;
        pointerY = (event.clientY - rect.top) / rect.height * 2 - 1;
        schedule();
      }, {passive:true, signal});
      this.addEventListener('pointerleave', () => { pointerX = pointerY = 0; schedule(); }, {signal});
      const updateMotion = () => {
        enabled = this.dataset.motion === 'true' && !reduce.matches;
        this.dataset.active = String(active && enabled && !document.hidden);
        if (!enabled) { depth?.removeAttribute('style'); silhouette?.removeAttribute('style'); }
        schedule();
      };
      reduce.addEventListener('change', updateMotion, {signal});
      document.addEventListener('visibilitychange', () => {
        this.dataset.active = String(active && enabled && !document.hidden);
      }, {signal});
      this.addEventListener('click', event => {
        if (event.target.closest('.gv-menu a')) this.querySelector('.gv-menu').open = false;
      }, {signal});
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          const menu = this.querySelector('.gv-menu');
          if (menu.open) { menu.open = false; menu.querySelector('summary').focus(); }
        }
      }, {signal});
      document.addEventListener('click', event => {
        const menu = this.querySelector('.gv-menu');
        if (menu.open && !menu.contains(event.target)) menu.open = false;
      }, {signal});
      this.cancelFrame = () => cancelAnimationFrame(frame);
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; this.observer?.disconnect(); this.cancelFrame?.(); }
  });
}

if (!customElements.get('ganvu-purchase')) {
  customElements.define('ganvu-purchase', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const {signal} = this.controller;
      const variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      const copy = JSON.parse(this.querySelector('[data-copy]').textContent);
      const quantity = this.querySelector('[name=quantity]');
      const variantField = this.querySelector('[name=id]');
      const packs = [...this.querySelectorAll('[data-pack]')];
      const submit = this.querySelector('[name=add]');
      let variant = variants.find(item => String(item.id) === variantField.value) || variants[0];
      const formatter = new Intl.NumberFormat(this.dataset.locale || 'he-IL', {style:'currency', currency:this.dataset.currency || 'ILS', minimumFractionDigits:0, maximumFractionDigits:2});
      const normalize = value => {
        const min = variant.min || 1, step = variant.step || 1;
        const lower = Math.max(min, Number.isFinite(value) ? value : min);
        const upper = variant.max ? Math.min(lower, variant.max) : lower;
        return min + Math.floor((upper-min)/step)*step;
      };
      const update = (sanitize = true) => {
        const amount = normalize(Number(quantity.value));
        if (sanitize) quantity.value = amount;
        this.querySelector('output').textContent = formatter.format(variant.price * amount / 100);
        for (const radio of packs) {
          const n = Number(radio.value);
          radio.checked = n === amount;
          radio.disabled = n < variant.min || (variant.max && n > variant.max) || (n-variant.min) % variant.step !== 0;
          radio.closest('label').querySelector('.gv-pack-price').textContent = formatter.format(variant.price*n/100);
        }
        this.querySelector('[data-quantity-step="-1"]').disabled = amount <= variant.min;
        this.querySelector('[data-quantity-step="1"]').disabled = !!variant.max && amount + variant.step > variant.max;
      };
      this.addEventListener('change', event => {
        if (event.target === variantField) {
          variant = variants.find(item => String(item.id) === variantField.value) || variants[0];
          quantity.min = variant.min; quantity.step = variant.step;
          if (variant.max) quantity.max = variant.max; else quantity.removeAttribute('max');
          submit.disabled = !variant.available;
          this.querySelector('[data-add-label]').textContent = variant.available ? copy.add : copy.sold;
        }
        if (event.target.matches('[data-pack]')) quantity.value = event.target.value;
        update();
      }, {signal});
      quantity.addEventListener('input', () => update(false), {signal});
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-quantity-step]');
        if (!button) return;
        quantity.value = normalize(Number(quantity.value)) + Number(button.dataset.quantityStep)*variant.step;
        quantity.dispatchEvent(new Event('change', {bubbles:true}));
      }, {signal});
      this.querySelector('form').addEventListener('submit', () => update(), {capture:true, signal});
      update();
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  });
}

(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const observer = new IntersectionObserver(entries => {
    for (const entry of entries) if (entry.isIntersecting) {
      entry.target.setAttribute('data-visible', '');
      entry.target.removeAttribute('data-pending');
      observer.unobserve(entry.target);
    }
  }, {threshold:.01});
  const init = root => {
    for (const element of root.querySelectorAll('.gv-reveal')) {
      if (!reduce.matches && !window.Shopify?.designMode && element.getBoundingClientRect().top > innerHeight - 40) {
        element.setAttribute('data-pending', ''); observer.observe(element);
      }
    }
  };
  init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const element of event.target.querySelectorAll('.gv-reveal')) observer.unobserve(element);
  });
  reduce.addEventListener('change', () => {
    if (reduce.matches) for (const element of document.querySelectorAll('.gv-reveal')) {
      element.removeAttribute('data-pending'); observer.unobserve(element);
    }
  });
})();
